import type { FilterDraft } from './useFilterDraft';
import { useFilterDraftContext } from './FilterDraftContext';

/**
 * FilterDraftBox (GTMOPS-2216) — the filter-popover analog of
 * `../views/ViewDirtyBox`: a persistent, non-blocking banner for an
 * edited-but-not-applied filter draft, shown by `FlowTable` once the Filters
 * popover is closed while `draft.isDirty` (see `useFilterDraft`'s module doc
 * for why the draft now survives the popover's own unmount-on-close).
 *
 * Reuses `ViewDirtyBox`'s exact card/border/color treatment (the
 * `border-content-warning/40` + `bg-bg-yellow-light` shell, same dark-mode
 * pair) so the two banners read as one visual language — this is
 * deliberately a SIBLING component, not a `ViewDirtyBox` prop extension:
 * `ViewDirtyBox` is wired to `FlowViewMachine` end to end (its buttons call
 * `machine.reset`/`save`/`saveAsNew` directly), and grafting an unrelated
 * three-button "filter draft" mode onto that contract would make it harder
 * to reason about either case, for a shell that's four Tailwind classes
 * copy-pasted here instead.
 *
 * Three actions, where `ViewDirtyBox` has two-or-three of its own — mapped
 * onto the SAME no-dual-primary hierarchy rule GTMOPS-2257 gave that
 * component, applied fresh here since three actions on THIS banner is a new
 * case, not a copy of an existing one:
 *  - **Apply** is filled-primary (`ViewDirtyBox`'s "Save" treatment) — a rep
 *    who edited a filter and didn't click Apply overwhelmingly meant to
 *    click away by accident, not to abandon the edit, so committing the
 *    draft is the common/expected action. Disabled (with a tooltip carrying
 *    the reason) while `draft.validationError` is set — the same value-shape
 *    gate the popover's own Apply button already enforces (Finding 2); this
 *    banner must never commit a malformed filter just because the popover
 *    happens to be closed.
 *  - **Edit** gets `ViewDirtyBox`'s "Save as new view" emphasized-secondary
 *    treatment (blue outline/tint) — a real, action-colored path, just not
 *    the default one. Re-opens the popover with the draft state intact (the
 *    caller wires `onEdit` to the same toggle that opens `ResponsivePopover`).
 *  - **Discard** gets `ViewDirtyBox`'s plain-secondary "Reset" treatment — an
 *    undo, not a write, reverting the draft to the last-applied filter via
 *    `draft.discard()`.
 *
 * DOM/visual order (Discard, Edit, Apply) runs low-to-high commitment,
 * left to right, ending on the primary action — a judgment call flagged for
 * product-owner review since the spec didn't pin an exact order.
 */
export interface FilterDraftBoxProps {
  /**
   * Phase 4: optional — `FlowTable` no longer passes this explicitly, since
   * the draft now lives in an enclosing `FilterDraftProvider` and this
   * component reads it off context instead (`useFilterDraftContext()`).
   * Still accepted for callers that own a draft directly and render this
   * box outside a provider (mirrors `FilterPopover`'s `draft` prop).
   */
  draft?: FilterDraft;
  /** Reopen the Filters popover with the draft state intact. */
  onEdit: () => void;
}

export function FilterDraftBox({ draft: externalDraft, onEdit }: FilterDraftBoxProps) {
  // Hooks must run unconditionally regardless of which source ends up
  // supplying the draft.
  const contextDraft = useFilterDraftContext();
  const draft = externalDraft ?? contextDraft;
  if (!draft) throw new Error('FilterDraftBox needs a draft prop or an enclosing FilterDraftProvider');

  const { isDirty, validationError, apply, discard } = draft;

  if (!isDirty) return null;

  return (
    <div
      data-testid="filter-draft-box"
      className="flex items-center gap-1.5 rounded-md border border-content-warning/40 bg-bg-yellow-light px-2 py-1 text-[11.5px] text-content-primary dark:border-amber-700/50 dark:text-nightshade-100"
    >
      <span className="font-semibold">Unapplied filter</span>
      <button
        type="button"
        onClick={discard}
        className="rounded border border-secondary bg-white px-1.5 py-0.5 text-content-secondary hover:bg-bg-secondary dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-400 dark:hover:bg-nightshade-700"
      >
        Discard
      </button>
      <button
        type="button"
        onClick={onEdit}
        className="rounded border border-content-action bg-bg-blue-light px-1.5 py-0.5 font-medium text-content-action hover:bg-bg-blue dark:border-blueberry-700 dark:text-blueberry-400"
      >
        Edit
      </button>
      <button
        type="button"
        onClick={apply}
        disabled={!!validationError}
        title={validationError ?? undefined}
        className="rounded border border-content-action bg-content-action px-1.5 py-0.5 text-white hover:bg-content-action-hover dark:border-blueberry-500 dark:bg-blueberry-500 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Apply
      </button>
    </div>
  );
}
